import {
    hapticFeedback,
    themeParams,
    useSignal,
} from "@telegram-apps/sdk-react";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface SegmentedOption {
    label: React.ReactNode;
    value: string | number;
}

type SegmentedControlProps = {
    options: SegmentedOption[];
    size?: "default" | "small";
    selectedValue: string | number;
    className?: string;
    onSelectedValueChange: (selectedValue: string | number) => void;
};

const SimpleSegmentedControl: React.FC<SegmentedControlProps> = ({
    options,
    className,
    size = "default",
    selectedValue,
    onSelectedValueChange,
}) => {
    const optionContainerRef = useRef<HTMLDivElement>(null);
    const isDark = useSignal(themeParams.isDark);

    const selectedIndex = useMemo(
        () => options.findIndex((item) => item.value === selectedValue),
        [options, selectedValue]
    );

    const selectedLabel = useMemo(
        () => options[selectedIndex]?.label,
        [options, selectedIndex]
    );

    const [selectedBtnSize, setSelectedBtnSize] = useState({
        width: 0,
        height: 0,
    });

    useEffect(() => {
        const resize = () => {
            const container = optionContainerRef.current;
            if (!container) return;

            const optionButWidth =
                container.firstElementChild?.clientWidth ?? 0;
            const optionButHeight =
                container.firstElementChild?.clientHeight ?? 0;
            setSelectedBtnSize({
                width: optionButWidth,
                height: optionButHeight,
            });
        };

        const visibilitychange = function () {
            if (document.visibilityState === "visible") {
                resize();
            }
        };

        resize();
        document.addEventListener("visibilitychange", visibilitychange);
        window.addEventListener("resize", resize);

        return () => {
            document.removeEventListener("visibilitychange", visibilitychange);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return (
        <div
            className={clsx(
                "relative whitespace-nowrap cursor-pointer",
                isDark ? "bg-bg_bottom" : "bg-bg_1",
                size === "default" ? "h-7 rounded-md" : "h-6 rounded",
                className
            )}
        >
            <div
                className="inline-flex items-center w-full h-full"
                ref={optionContainerRef}
            >
                {options.map((option, index) => {
                    const showSeparateLine =
                        index !== 0 &&
                        selectedIndex !== index - 1 &&
                        selectedIndex !== index;
                    return (
                        <div
                            key={option.value}
                            className="relative h-full w-0 flex-1 flex items-center justify-center"
                            onClick={() => {
                                hapticFeedback.impactOccurred("light");
                                onSelectedValueChange(option.value);
                            }}
                        >
                            <span className="text-text_1 text-direction1">
                                {option.label}
                            </span>
                            {showSeparateLine && (
                                <span className="absolute left-0 w-[1px] h-3 bg-border_2 rounded -ml-[0.5px]" />
                            )}
                        </div>
                    );
                })}
            </div>
            {selectedBtnSize.width > 0 && (
                <div
                    className={clsx(
                        "absolute -top-[0.5px] left-0 bg-bg_top border-[0.5px] border-border_2 box-border",
                        "flex items-center justify-center transition-transform transform",
                        size === "default" ? "rounded-md" : "rounded"
                    )}
                    style={
                        {
                            width: selectedBtnSize.width,
                            height: selectedBtnSize.height,
                            "--tw-translate-x": `${selectedIndex * selectedBtnSize.width}px`,
                        } as React.CSSProperties
                    }
                >
                    <span className="text-text_1 text-direction1 font-semibold">
                        {selectedLabel}
                    </span>
                </div>
            )}
        </div>
    );
};

export default SimpleSegmentedControl;
